import { connect, useDispatch } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";

import { State } from "redux/store";
import { fetchHealthStatus } from "redux/Datasets/actions";
import { isAnonymousUser, selectUser, userLogout } from "redux/authReducer";
import { getUserInfo } from "redux/User/selectors";

import SelectPlanModal from "views/modals/SelectPlanModal";
import ToastWrapper from "views/components/ToastWrapper";
import Cart from "views/components/Cart";
import VLColorfulLoader from "views/uikit/VLColorfulLoader";
import Sidebar from "./Sidebar";
import ReactGA from "react-ga4";
import { SavedView } from "types";

import {
  INVITATION_TOKEN_PARAMETER,
  SAVED_VIEW_PARAMETER,
} from "helpers/constants/miscellaneous";
import { isFastdupUser } from "helpers/utility/utilities";
import { PUBLIC_DATASETS_PATH } from "helpers/constants/paths";

import styles from "./style.module.scss";
import DatasetSharingMenu from "views/components/SharingMenu/DatasetSharingMenu";
import { acceptInvitation, fetchSavedViews } from "redux/SingleDataset/actions";
import { amplitudeTrack } from "helpers/utility/amplitude";
import useNavigateToView from "hooks/useNavigateToView";
import { useFeatureFlagService } from "hooks/useFeatureFlagService";
import { DATASET_SHARE } from "helpers/constants/filters";
import {
  AMP_DATASET_SHARE_INVITE_ACCEPT,
  AMP_USER_SIGN_IN,
  AMP_USER_SIGN_UP,
} from "helpers/constants/amplitudeEvents";
import {
  AMP_NEW_USER,
  AMP_USER_ORIGIN,
} from "helpers/constants/amplitudeProperties";
import {
  LinkedInConversionId,
  trackLinkedInConversion,
} from "helpers/utility/linkedinAnalytics";
import { THEME } from "views/components/ThemeToggle";
import { getShowBugReportModal } from "redux/Modals/selectors";
import classNames from "classnames";
import { ConfigProvider } from "../providers/ConfigProvider";
import { UserConfigProvider } from "contexts/UserConfigContext";
import { DeleteDatasetProvider } from "../providers/DeleteDatasetProvider/DeleteDatasetProvider";

function SharingMenuWrapper() {
  const isSharingEnabled = useFeatureFlagService(DATASET_SHARE).enabled;
  return isSharingEnabled ? <DatasetSharingMenu /> : null;
}

function Layout({ userAvatar, showBugReporter }: LayoutTypes) {
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { datasetId } = useParams();
  const navigateToView = useNavigateToView();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const isPublicPath = location.pathname === PUBLIC_DATASETS_PATH;

  const navigateToViewIfSavedViewParameter = async () => {
    if (searchParams.get(SAVED_VIEW_PARAMETER)) {
      const views = await dispatch(fetchSavedViews(datasetId) as any);
      if (views && views.length > 0) {
        const view = views.find(
          (view: SavedView) =>
            view.id === searchParams.get(SAVED_VIEW_PARAMETER),
        );
        navigateToView(view);
      }
    }
  };

  const grantAccessForInvite = async () => {
    const invitationToken = searchParams.get(INVITATION_TOKEN_PARAMETER);
    if (datasetId && invitationToken) {
      const user = selectUser();
      if ((user && isAnonymousUser()) || !user) {
        // If user is not logged in we can't grant them access, so we prompt them to sign in.
        userLogout();
      }
      await dispatch(acceptInvitation(datasetId, invitationToken) as any);
      amplitudeTrack(AMP_DATASET_SHARE_INVITE_ACCEPT, {
        [AMP_NEW_USER]: searchParams.get("nuse") === "1",
      });
      searchParams.delete(INVITATION_TOKEN_PARAMETER);
      navigate({ search: `?${searchParams.toString()}` }, { replace: true });
    }
  };

  const reportAnalytics = () => {
    if (searchParams.get("lin") === "1") {
      let user = selectUser();
      amplitudeTrack(AMP_USER_SIGN_IN, {
        [AMP_USER_ORIGIN]: user?.origin,
      });
      searchParams.delete("lin");
    }
    if (searchParams.get("nuse") === "1") {
      amplitudeTrack(AMP_USER_SIGN_UP);
      trackLinkedInConversion(LinkedInConversionId.SignUp);
      ReactGA.event({ category: "User", action: "Sign Up" });
      searchParams.delete("nuse");
    }
    navigate({ search: `?${searchParams.toString()}` });
  };

  useEffect(() => {
    const initializeLayout = async () => {
      reportAnalytics();
      await grantAccessForInvite();
      const authenticationDisabled =
        process.env.REACT_APP_IS_AUTHENTICATION_DISABELED?.trim() === "false";
      console.log(
        "auth",
        process.env.REACT_APP_IS_AUTHENTICATION_DISABELED === "false",
      );
      const fastdupUser = isFastdupUser();

      if (
        !datasetId &&
        isAnonymousUser() &&
        !isPublicPath &&
        authenticationDisabled &&
        !fastdupUser
      ) {
        userLogout();
      } else {
        await navigateToViewIfSavedViewParameter();
      }

      setLoading(false);
    };
    initializeLayout();

    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    const repeatedHealthCheck = setInterval(() => {
      dispatch(fetchHealthStatus() as any);
    }, 60000);

    dispatch(fetchHealthStatus() as any);

    return () => clearInterval(repeatedHealthCheck);
  }, [dispatch]);

  useEffect(() => {
    const initialSavedTheme =
      (localStorage.getItem("theme") as THEME) || THEME.DARK;

    if (initialSavedTheme === THEME.LIGHT) {
      document.body.setAttribute("data-theme", THEME.LIGHT);
      localStorage.setItem("theme", THEME.LIGHT);
    } else {
      document.body.setAttribute("data-theme", THEME.DARK);
      localStorage.setItem("theme", THEME.DARK);
    }
  }, []);

  if (!loading) {
    return (
      <ConfigProvider>
        <UserConfigProvider>
          <DeleteDatasetProvider>
            <ToastWrapper />
            <div className={styles.mainWrapper}>
              <div className={styles.innerWrapper}>
                <Sidebar
                  userAvatar={userAvatar}
                  showBugReporter={showBugReporter}
                />
                <div className={styles.content}>
                  <div className={classNames(styles.body)}>
                    <Outlet />
                  </div>
                </div>
              </div>
            </div>
            <Cart />
            <SharingMenuWrapper />
            <SelectPlanModal />
          </DeleteDatasetProvider>
        </UserConfigProvider>
      </ConfigProvider>
    );
  }
  return (
    <div className={styles.loadingPage}>
      <VLColorfulLoader />
    </div>
  );
}

const mapStateToProps = (state: State) => {
  const userInfo = getUserInfo(state);
  return {
    userAvatar: userInfo.userAvatar,
    showBugReporter: getShowBugReportModal(state),
  };
};

export default connect(mapStateToProps)(Layout);

type LayoutTypes = {
  userAvatar?: string;
  showBugReporter: boolean;
};
